import { useRef, useMemo, useState, useEffect } from 'preact/hooks';
import { CurrentItemProps, CurrentKeyDetail, Item } from '#UNSAFE_Collection';
import { useReorderableContext } from '#hooks/PRIVATE_useReorderable/useReorderableContext';

/**
 * This hook converts the new API (i.e. currentItemOverride and onPersistCurrentItem) back to
 * the old API (i.e. currentKey and onCurrentKeyChange), keeps the internal current item, and
 * also triggers an update when a new currentItemOverride is passed by the app
 *
 * @param currentItemOverride
 * @param onPersistCurrentItem
 */
export const useCurrentItemOverride = <K extends string | number>(
  currentItemOverride: CurrentItemProps<K>['currentItemOverride'],
  onPersistCurrentItem: CurrentItemProps<K>['onPersistCurrentItem']
) => {
  // the internal state of current item
  const [currentItem, setCurrentItem] = useState<Item<K>>();
  // track the currentItemOverride props
  const currentItemOverrideRef = useRef<Item<K>>();
  // whether the current item is overridden by the app
  const isCurrentItemOverridden = useRef(false);

  // if reorder is enabled, need to update the context here when current item is changed
  const reorderContext = useReorderableContext();

  // intercept onPersistCurrentItem calls to ensure internal state is updated appropriately
  const handleCurrentItemChanged = useMemo(() => {
    return (detail: CurrentKeyDetail<K>) => {
      const rowKey = detail.value;
      setCurrentItem({ rowKey });
      onPersistCurrentItem?.(detail);
      if (reorderContext?.currentItemKey != null) {
        reorderContext.currentItemKey.current = rowKey;
      }
    };
  }, [onPersistCurrentItem, reorderContext?.currentItemKey]);

  // ensure any new currentItemOverride settings are applied only on that render
  // (same as how Table handles the changes)
  if (currentItemOverrideRef.current !== currentItemOverride) {
    currentItemOverrideRef.current = currentItemOverride;
    if (currentItem?.rowKey !== currentItemOverride?.rowKey) {
      isCurrentItemOverridden.current = true;
      handleCurrentItemChanged({ value: currentItemOverride?.rowKey as K });
    }
  }

  useEffect(() => {
    isCurrentItemOverridden.current = false;
  });

  return {
    currentKey: currentItem?.rowKey,
    onCurrentKeyChange: handleCurrentItemChanged,
    isCurrentItemOverridden: isCurrentItemOverridden.current
  };
};
